/**
 * Usage-analytics event queue: batches events and ships them to
 * `POST /api/events`. See features/usage_analytics.md.
 *
 * Best-effort by design — a failed send is dropped, never retried or
 * surfaced, and never redirects to sign-in (it uses a raw fetch, not the
 * typed `request` wrapper). The server allowlists event names and props, so
 * an event added here must also be added to `app/services/analytics.py`.
 */

import { apiUrl } from "@/api/client";

export type EventProps = Record<string, string | number | boolean>;

interface QueuedEvent {
  event: string;
  props?: EventProps;
}

const FLUSH_MS = 10_000;
const PING_MS = 60_000;
const MAX_BATCH = 50;
// Only reached if sending never starts (e.g. signed out); stops unbounded growth.
const MAX_QUEUE = 500;

const session = Math.random().toString(36).slice(2, 12) + Date.now().toString(36);

let queue: QueuedEvent[] = [];
let started = false;
// Follows the user's opt-out. Events queue from page load but nothing is sent
// until startAnalytics(), which App calls after applying the user's choice;
// the server also drops events from anyone who opted out.
let enabled = true;
const hideHandlers: Array<() => void> = [];

/** Follow the user's opt-out. Turning off discards anything still queued. */
export function setAnalyticsEnabled(on: boolean): void {
  enabled = on;
  if (!on) queue = [];
}

export function track(event: string, props?: EventProps): void {
  if (!enabled || queue.length >= MAX_QUEUE) return;
  queue.push(props && Object.keys(props).length ? { event, props } : { event });
  if (started && queue.length >= MAX_BATCH) flush();
}

export function flush(): void {
  if (!started || !enabled) return;
  while (queue.length) {
    const events = queue.splice(0, MAX_BATCH);
    // keepalive lets the request finish while the page is being hidden or
    // closed, which is when the last batch (and dwell times) are sent.
    fetch(apiUrl("/api/events"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session, events }),
      credentials: "include",
      keepalive: true,
    }).catch(() => {
      /* offline / transient — drop */
    });
  }
}

/** Run `fn` just before the page is hidden, ahead of the final flush. */
export function onPageHide(fn: () => void): void {
  hideHandlers.push(fn);
}

function referrerHost(): string {
  try {
    return document.referrer ? new URL(document.referrer).host : "";
  } catch {
    return "";
  }
}

/** Start sending. Call once the user is signed in; later calls are no-ops. */
export function startAnalytics(): void {
  if (started) return;
  started = true;
  track("session_start", {
    viewport_w: window.innerWidth,
    viewport_h: window.innerHeight,
    referrer: referrerHost(),
  });
  flush();
  window.setInterval(flush, FLUSH_MS);
  // Active time = pings × 60s. Only counted while the tab is visible — the
  // same rule as the presence heartbeat — so a background tab adds nothing.
  window.setInterval(() => {
    if (document.visibilityState === "visible") track("active_ping");
  }, PING_MS);
  const hide = () => {
    hideHandlers.forEach((fn) => fn());
    flush();
  };
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") hide();
  });
  window.addEventListener("pagehide", hide);
}
